// Deposits, prepayment, refunds and the cancellation terms customers accept before paying.
// Kept apart from the main bundle, like the legal copy, and deep-merged into it in i18n.js.
// The binding terms themselves are worded by the API (so the stored text matches what was
// shown); these strings frame them.

export const prepaymentTranslationsEn = {
  customersManagement: {
    details: {
      onlinePayments: {
        title: 'Payments',
        label: 'Allow online payment',
        help: 'When off, this customer only sees paying at the store, for bookings and memberships. Services that ask for a deposit are booked without one.',
        badge: 'Pays in store only',
      },
      refunds: {
        title: 'Refunds',
        metric: 'Refunded',
        count_one: '{{count}} refund',
        count_other: '{{count}} refunds',
        appointmentOn: 'Appointment on {{date}}',
        refundedOn: 'Refunded on {{date}}',
        triggers: {
          customerCancellation: 'Cancelled by the customer',
          storeCancellation: 'Cancelled by the store',
          manual: 'Manual refund',
          paymentAfterCancellation: 'Paid after it was cancelled',
        },
      },
    },
  },
  servicesManagement: {
    card: {
      deposit: 'Deposit {{amount}}',
      fullPrepayment: 'Paid online in advance',
    },
    validation: {
      depositRequired: 'Enter a deposit greater than 0.',
      depositPercentageRange: 'Enter a percentage between 1 and 100.',
      depositExceedsPrice: 'The deposit cannot be higher than the service price.',
    },
    prepayment: {
      title: 'Online prepayment',
      help: 'Ask customers to pay part or all of this service online when they book. Useful for longer or more expensive services.',
      onlinePaymentsRequired: 'Connect Stripe and turn on online payment for services before asking for a deposit.',
      onlinePaymentsOffWarning: 'Online payment for services is off, so customers cannot pay this deposit. Switch it back on or set this service to no prepayment.',
      openPaymentSettings: 'Open payment settings',
      mode: 'Prepayment',
      modeNotRequired: 'No prepayment',
      modeDeposit: 'Deposit',
      modeFull: 'Full payment',
      modeNotRequiredHelp: 'The customer chooses how to pay. If they pay online and cancel in time, they get everything back except the payment processing fee.',
      modeDepositHelp: 'The customer pays a deposit online to book, and can choose to pay in full. The deposit is not refunded if they cancel.',
      modeFullHelp: 'The customer must pay the full price online to book. You can name a part that is not refunded if they cancel.',
      depositType: 'Deposit type',
      depositTypeFixed: 'Fixed amount ({{currency}})',
      depositTypePercentage: 'Percentage of the price',
      depositValue: 'Deposit',
      nonRefundableValue: 'Non-refundable part (optional)',
      previewTitle: 'What the customer sees',
      previewSplit: 'Pay {{now}} now and {{later}} at the store.',
      previewFull: 'Pay {{now}} now.',
      previewNonRefundable: '{{amount}} is not refunded if the customer cancels.',
      previewFeeOnly: 'If the customer cancels in time, they are refunded minus the payment processing fee.',
      previewNeedsPrice: 'Set a price to see what the customer will pay.',
      allowInstallments: 'Offer installment payments (Klarna)',
      errors: {
        invalid: 'The deposit must be greater than 0 and no higher than the service price.',
        belowProcessingFee: 'The deposit is too small to cover the payment processing fee for this service. Increase it and try again.',
        onlinePaymentsRequired: 'Deposits need Stripe connected and online payment for services turned on.',
      },
    },
  },
  customer: {
    appointments: {
      booking: {
        confirmAndPay: 'Confirm and pay',
        policy: {
          title: 'Payment and cancellation',
          loading: 'Working out what you pay now…',
          error: 'We could not load the payment terms. Please try again.',
          payDepositOption: 'Pay {{now}} deposit now, {{later}} at the store',
          payFullOption: 'Pay {{now}} in full now',
          depositNotice: '{{amount}} of this payment is a non-refundable deposit.',
          payNow: 'Pay now',
          payAtStore: 'Pay at the store',
          accept: 'I have read and accept these payment and cancellation terms.',
          acceptRequired: 'Please accept the payment and cancellation terms to continue.',
          prepaymentRequired: 'One of the selected services must be paid online when booking.',
          prepaymentNotice: '{{services}} must be paid online when booking, so paying at the store is not available for this booking.',
          checkoutSubtitle: 'Up to {{amount}} of this payment is not refunded if you cancel.',
          payDepositNow: 'Pay {{now}} deposit now',
          restAtStore: 'and {{later}} at the store',
          acceptLead: 'I accept the',
          termsLink: 'payment and cancellation terms',
          acceptAria: 'I accept the payment and cancellation terms',
          termsTitle: 'Payment and cancellation terms',
          acceptButton: 'I accept',
        },
      },
      myAppointments: {
        depositPaidNote: 'Deposit paid online: {{amount}}. Pay the rest at the store or online.',
        payBalance: 'Pay the rest online',
        refundedNote: 'Refunded to your original payment method.',
        partiallyRefundedNote: 'Partly refunded to your original payment method.',
        refund: {
          loading: 'Checking your refund…',
          error: 'We could not check your refund right now. You can still cancel, and anything due will be refunded automatically.',
          paid: 'You paid online',
          kept: 'Not refunded',
          youGetBack: 'You get back',
          retainedDeposit: 'This is your deposit, which is not refundable.',
          retainedFee: 'This covers the payment processing fee the store was charged and cannot recover.',
          retainedLate: 'Cancellations less than the notice period before the appointment are not refunded.',
          timing: 'Refunds usually take 5 to 10 business days to reach your account.',
          lateWarning: 'This appointment starts in less than {{hours}} hours. If you cancel now, what you paid online will not be refunded.',
          lateNothingPaid: 'This appointment starts in less than {{hours}} hours. Please let the store know if you can.',
          blockedTooLate: 'This appointment can no longer be cancelled online because it starts in less than {{hours}} hours. Please contact the store.',
          blockedStarted: 'This appointment has already started.',
          blockedCancelled: 'This appointment is already cancelled.',
          blockedCompleted: 'This appointment is already completed.',
          blockedGeneric: 'This appointment can no longer be cancelled online. Please contact the store.',
          successWithRefund: 'Appointment cancelled. {{amount}} will be refunded to your original payment method.',
          successPending: 'Appointment cancelled. Your refund is being processed and will reach your original payment method.',
        },
        payTerms: {
          title: 'Before you pay',
          amount: 'You are about to pay {{amount}} online.',
          continue: 'Continue to payment',
        },
      },
    },
  },
  managementAppointments: {
    paymentStatus: {
      depositPaid: 'Deposit paid',
      refunded: 'Refunded',
      partiallyRefunded: 'Partly refunded',
      disputed: 'Disputed',
    },
    prepayment: {
      paidOnline: 'Paid online',
      nonRefundableDeposit: 'Non-refundable deposit',
      balanceDueInStore: 'The rest is due at the store.',
      productsPaidOnline: 'Products paid online',
      productsPaidOnlineLocked: 'The customer already paid for these products online, so they cannot be changed. Refund the online payment first if they need to change.',
    },
    confirm: {
      cancelPaidOnlineMessage: 'The customer paid online and will be refunded in full, including the payment processing fee, which your store absorbs. This frees the time slot and cannot be undone.',
      noShowPaidOnlineMessage: 'The customer paid online. Marking a no-show keeps that payment and nothing is refunded.',
    },
    delete: {
      paidOnline: 'This appointment was paid online. Cancel it instead, so the customer is refunded.',
    },
  },
  paymentSettingsPage: {
    customerPayments: {
      error: {
        prepaymentServices: 'Some services ask for a deposit or prepayment. Set them to no prepayment before turning off online payment for services.',
      },
      statementDescriptor: {
        title: 'Name on bank statements',
        help: 'This is what customers see on their bank or card statement. Use the name they know your store by, so they recognise the charge.',
        label: 'Statement name',
        preview: 'Customers will see',
        invalid: 'Use 5 to 22 letters, numbers or spaces, including at least one letter.',
        save: 'Save statement name',
        success: 'Statement name saved.',
        error: 'The statement name could not be saved. Try a shorter name without symbols.',
      },
    },
  },
};

export const prepaymentTranslationsPt = {
  customersManagement: {
    details: {
      onlinePayments: {
        title: 'Pagamentos',
        label: 'Permitir pagamento online',
        help: 'Quando desativado, este cliente só vê a opção de pagar no estabelecimento, para marcações e planos. Os serviços que pedem sinal são marcados sem sinal.',
        badge: 'Paga só no estabelecimento',
      },
      refunds: {
        title: 'Reembolsos',
        metric: 'Reembolsado',
        count_one: '{{count}} reembolso',
        count_other: '{{count}} reembolsos',
        appointmentOn: 'Marcação de {{date}}',
        refundedOn: 'Reembolsado a {{date}}',
        triggers: {
          customerCancellation: 'Cancelada pelo cliente',
          storeCancellation: 'Cancelada pelo estabelecimento',
          manual: 'Reembolso manual',
          paymentAfterCancellation: 'Paga depois de cancelada',
        },
      },
    },
  },
  servicesManagement: {
    card: {
      deposit: 'Sinal {{amount}}',
      fullPrepayment: 'Pago online antecipadamente',
    },
    validation: {
      depositRequired: 'Introduza um sinal superior a 0.',
      depositPercentageRange: 'Introduza uma percentagem entre 1 e 100.',
      depositExceedsPrice: 'O sinal não pode ser superior ao preço do serviço.',
    },
    prepayment: {
      title: 'Pré-pagamento online',
      help: 'Peça aos clientes que paguem parte ou a totalidade deste serviço online ao marcar. Útil para serviços mais longos ou mais caros.',
      onlinePaymentsRequired: 'Ligue o Stripe e ative o pagamento online de serviços antes de pedir um sinal.',
      onlinePaymentsOffWarning: 'O pagamento online de serviços está desativado, por isso os clientes não conseguem pagar este sinal. Volte a ativá-lo ou defina este serviço sem pré-pagamento.',
      openPaymentSettings: 'Abrir definições de pagamento',
      mode: 'Pré-pagamento',
      modeNotRequired: 'Sem pré-pagamento',
      modeDeposit: 'Sinal',
      modeFull: 'Pagamento total',
      modeNotRequiredHelp: 'O cliente escolhe como pagar. Se pagar online e cancelar a tempo, recebe tudo de volta exceto a taxa de processamento do pagamento.',
      modeDepositHelp: 'O cliente paga um sinal online para marcar e pode optar por pagar a totalidade. O sinal não é devolvido se cancelar.',
      modeFullHelp: 'O cliente tem de pagar o preço total online para marcar. Pode indicar uma parte que não é devolvida se cancelar.',
      depositType: 'Tipo de sinal',
      depositTypeFixed: 'Valor fixo ({{currency}})',
      depositTypePercentage: 'Percentagem do preço',
      depositValue: 'Sinal',
      nonRefundableValue: 'Parte não reembolsável (opcional)',
      previewTitle: 'O que o cliente vê',
      previewSplit: 'Paga {{now}} agora e {{later}} no estabelecimento.',
      previewFull: 'Paga {{now}} agora.',
      previewNonRefundable: '{{amount}} não é devolvido se o cliente cancelar.',
      previewFeeOnly: 'Se o cliente cancelar a tempo, é reembolsado menos a taxa de processamento do pagamento.',
      previewNeedsPrice: 'Defina um preço para ver quanto o cliente vai pagar.',
      allowInstallments: 'Permitir pagamento a prestações (Klarna)',
      errors: {
        invalid: 'O sinal tem de ser superior a 0 e não pode ser superior ao preço do serviço.',
        belowProcessingFee: 'O sinal é demasiado baixo para cobrir a taxa de processamento do pagamento deste serviço. Aumente-o e tente novamente.',
        onlinePaymentsRequired: 'Os sinais exigem o Stripe ligado e o pagamento online de serviços ativo.',
      },
    },
  },
  customer: {
    appointments: {
      booking: {
        confirmAndPay: 'Confirmar e pagar',
        policy: {
          title: 'Pagamento e cancelamento',
          loading: 'A calcular quanto paga agora…',
          error: 'Não foi possível carregar as condições de pagamento. Tente novamente.',
          payDepositOption: 'Pagar {{now}} de sinal agora e {{later}} no estabelecimento',
          payFullOption: 'Pagar {{now}} na totalidade agora',
          depositNotice: '{{amount}} deste pagamento é um sinal não reembolsável.',
          payNow: 'Pagar agora',
          payAtStore: 'Pagar no estabelecimento',
          accept: 'Li e aceito estas condições de pagamento e cancelamento.',
          acceptRequired: 'Aceite as condições de pagamento e cancelamento para continuar.',
          prepaymentRequired: 'Um dos serviços selecionados tem de ser pago online na marcação.',
          prepaymentNotice: '{{services}} tem de ser pago online na marcação, por isso o pagamento no estabelecimento não está disponível para esta marcação.',
          checkoutSubtitle: 'Até {{amount}} deste pagamento não é devolvido se cancelar.',
          payDepositNow: 'Pagar {{now}} de sinal agora',
          restAtStore: 'e {{later}} no estabelecimento',
          acceptLead: 'Aceito as',
          termsLink: 'condições de pagamento e cancelamento',
          acceptAria: 'Aceito as condições de pagamento e cancelamento',
          termsTitle: 'Condições de pagamento e cancelamento',
          acceptButton: 'Aceito',
        },
      },
      myAppointments: {
        depositPaidNote: 'Sinal pago online: {{amount}}. Pague o restante no estabelecimento ou online.',
        payBalance: 'Pagar restante online',
        refundedNote: 'Reembolsado para o método de pagamento original.',
        partiallyRefundedNote: 'Parcialmente reembolsado para o método de pagamento original.',
        refund: {
          loading: 'A verificar o seu reembolso…',
          error: 'Não foi possível verificar o seu reembolso neste momento. Pode cancelar na mesma e o valor devido será reembolsado automaticamente.',
          paid: 'Pagou online',
          kept: 'Não reembolsado',
          youGetBack: 'Recebe de volta',
          retainedDeposit: 'Este é o seu sinal, que não é reembolsável.',
          retainedFee: 'Cobre a taxa de processamento do pagamento que foi cobrada ao estabelecimento e que não é recuperável.',
          retainedLate: 'Os cancelamentos feitos com menos antecedência do que a exigida não são reembolsados.',
          timing: 'Os reembolsos demoram normalmente 5 a 10 dias úteis a chegar à sua conta.',
          lateWarning: 'Esta marcação começa em menos de {{hours}} horas. Se cancelar agora, o valor pago online não será devolvido.',
          lateNothingPaid: 'Esta marcação começa em menos de {{hours}} horas. Se puder, avise o estabelecimento.',
          blockedTooLate: 'Esta marcação já não pode ser cancelada online porque começa em menos de {{hours}} horas. Contacte o estabelecimento.',
          blockedStarted: 'Esta marcação já começou.',
          blockedCancelled: 'Esta marcação já está cancelada.',
          blockedCompleted: 'Esta marcação já está concluída.',
          blockedGeneric: 'Esta marcação já não pode ser cancelada online. Contacte o estabelecimento.',
          successWithRefund: 'Marcação cancelada. {{amount}} será reembolsado para o método de pagamento original.',
          successPending: 'Marcação cancelada. O seu reembolso está a ser processado e chegará ao método de pagamento original.',
        },
        payTerms: {
          title: 'Antes de pagar',
          amount: 'Vai pagar {{amount}} online.',
          continue: 'Continuar para o pagamento',
        },
      },
    },
  },
  managementAppointments: {
    paymentStatus: {
      depositPaid: 'Sinal pago',
      refunded: 'Reembolsado',
      partiallyRefunded: 'Parcialmente reembolsado',
      disputed: 'Em disputa',
    },
    prepayment: {
      paidOnline: 'Pago online',
      nonRefundableDeposit: 'Sinal não reembolsável',
      balanceDueInStore: 'O restante é pago no estabelecimento.',
      productsPaidOnline: 'Produtos pagos online',
      productsPaidOnlineLocked: 'O cliente já pagou estes produtos online, por isso não podem ser alterados. Reembolse primeiro o pagamento online se for preciso alterá-los.',
    },
    confirm: {
      cancelPaidOnlineMessage: 'O cliente pagou online e será reembolsado na totalidade, incluindo a taxa de processamento do pagamento, que fica a cargo do estabelecimento. Isto liberta o horário e não pode ser revertido.',
      noShowPaidOnlineMessage: 'O cliente pagou online. Ao marcar como não comparência, o pagamento é mantido e nada é reembolsado.',
    },
    delete: {
      paidOnline: 'Esta marcação foi paga online. Cancele-a em vez de a eliminar, para que o cliente seja reembolsado.',
    },
  },
  paymentSettingsPage: {
    customerPayments: {
      error: {
        prepaymentServices: 'Alguns serviços pedem sinal ou pré-pagamento. Defina-os sem pré-pagamento antes de desativar o pagamento online de serviços.',
      },
      statementDescriptor: {
        title: 'Nome no extrato bancário',
        help: 'É o que os clientes veem no extrato do banco ou do cartão. Use o nome pelo qual conhecem o seu estabelecimento, para que reconheçam o pagamento.',
        label: 'Nome no extrato',
        preview: 'Os clientes vão ver',
        invalid: 'Use 5 a 22 letras, números ou espaços, incluindo pelo menos uma letra.',
        save: 'Guardar nome no extrato',
        success: 'Nome no extrato guardado.',
        error: 'Não foi possível guardar o nome no extrato. Experimente um nome mais curto e sem símbolos.',
      },
    },
  },
};

export const prepaymentTranslationsEs = {
  customersManagement: {
    details: {
      onlinePayments: {
        title: 'Pagos',
        label: 'Permitir pago online',
        help: 'Si está desactivado, este cliente solo ve la opción de pagar en el establecimiento, para citas y planes. Los servicios que piden señal se reservan sin señal.',
        badge: 'Paga solo en el establecimiento',
      },
      refunds: {
        title: 'Reembolsos',
        metric: 'Reembolsado',
        count_one: '{{count}} reembolso',
        count_other: '{{count}} reembolsos',
        appointmentOn: 'Cita del {{date}}',
        refundedOn: 'Reembolsado el {{date}}',
        triggers: {
          customerCancellation: 'Cancelada por el cliente',
          storeCancellation: 'Cancelada por el establecimiento',
          manual: 'Reembolso manual',
          paymentAfterCancellation: 'Pagada después de cancelarse',
        },
      },
    },
  },
  servicesManagement: {
    card: {
      deposit: 'Señal {{amount}}',
      fullPrepayment: 'Pagado online por adelantado',
    },
    validation: {
      depositRequired: 'Introduce una señal mayor que 0.',
      depositPercentageRange: 'Introduce un porcentaje entre 1 y 100.',
      depositExceedsPrice: 'La señal no puede ser mayor que el precio del servicio.',
    },
    prepayment: {
      title: 'Pago anticipado online',
      help: 'Pide a los clientes que paguen parte o la totalidad de este servicio online al reservar. Útil para servicios más largos o más caros.',
      onlinePaymentsRequired: 'Conecta Stripe y activa el pago online de servicios antes de pedir una señal.',
      onlinePaymentsOffWarning: 'El pago online de servicios está desactivado, así que los clientes no pueden pagar esta señal. Vuelve a activarlo o configura este servicio sin pago anticipado.',
      openPaymentSettings: 'Abrir ajustes de pago',
      mode: 'Pago anticipado',
      modeNotRequired: 'Sin pago anticipado',
      modeDeposit: 'Señal',
      modeFull: 'Pago completo',
      modeNotRequiredHelp: 'El cliente elige cómo pagar. Si paga online y cancela a tiempo, se le devuelve todo excepto la comisión de procesamiento del pago.',
      modeDepositHelp: 'El cliente paga una señal online para reservar y puede elegir pagar el total. La señal no se devuelve si cancela.',
      modeFullHelp: 'El cliente debe pagar el precio completo online para reservar. Puedes indicar una parte que no se devuelve si cancela.',
      depositType: 'Tipo de señal',
      depositTypeFixed: 'Importe fijo ({{currency}})',
      depositTypePercentage: 'Porcentaje del precio',
      depositValue: 'Señal',
      nonRefundableValue: 'Parte no reembolsable (opcional)',
      previewTitle: 'Lo que ve el cliente',
      previewSplit: 'Paga {{now}} ahora y {{later}} en el establecimiento.',
      previewFull: 'Paga {{now}} ahora.',
      previewNonRefundable: '{{amount}} no se devuelve si el cliente cancela.',
      previewFeeOnly: 'Si el cliente cancela a tiempo, se le reembolsa menos la comisión de procesamiento del pago.',
      previewNeedsPrice: 'Define un precio para ver cuánto pagará el cliente.',
      allowInstallments: 'Permitir pago a plazos (Klarna)',
      errors: {
        invalid: 'La señal debe ser mayor que 0 y no puede superar el precio del servicio.',
        belowProcessingFee: 'La señal es demasiado baja para cubrir la comisión de procesamiento del pago de este servicio. Auméntala e inténtalo de nuevo.',
        onlinePaymentsRequired: 'Las señales requieren Stripe conectado y el pago online de servicios activado.',
      },
    },
  },
  customer: {
    appointments: {
      booking: {
        confirmAndPay: 'Confirmar y pagar',
        policy: {
          title: 'Pago y cancelación',
          loading: 'Calculando cuánto pagas ahora…',
          error: 'No hemos podido cargar las condiciones de pago. Inténtalo de nuevo.',
          payDepositOption: 'Pagar {{now}} de señal ahora y {{later}} en el establecimiento',
          payFullOption: 'Pagar {{now}} en total ahora',
          depositNotice: '{{amount}} de este pago es una señal no reembolsable.',
          payNow: 'Pagar ahora',
          payAtStore: 'Pagar en el establecimiento',
          accept: 'He leído y acepto estas condiciones de pago y cancelación.',
          acceptRequired: 'Acepta las condiciones de pago y cancelación para continuar.',
          prepaymentRequired: 'Uno de los servicios seleccionados debe pagarse online al reservar.',
          prepaymentNotice: '{{services}} debe pagarse online al reservar, así que el pago en el establecimiento no está disponible para esta reserva.',
          checkoutSubtitle: 'Hasta {{amount}} de este pago no se devuelve si cancelas.',
          payDepositNow: 'Pagar {{now}} de señal ahora',
          restAtStore: 'y {{later}} en el establecimiento',
          acceptLead: 'Acepto las',
          termsLink: 'condiciones de pago y cancelación',
          acceptAria: 'Acepto las condiciones de pago y cancelación',
          termsTitle: 'Condiciones de pago y cancelación',
          acceptButton: 'Acepto',
        },
      },
      myAppointments: {
        depositPaidNote: 'Señal pagada online: {{amount}}. Paga el resto en el establecimiento u online.',
        payBalance: 'Pagar el resto online',
        refundedNote: 'Reembolsado a tu método de pago original.',
        partiallyRefundedNote: 'Reembolsado en parte a tu método de pago original.',
        refund: {
          loading: 'Comprobando tu reembolso…',
          error: 'Ahora mismo no podemos comprobar tu reembolso. Puedes cancelar igualmente y lo que corresponda se reembolsará automáticamente.',
          paid: 'Pagaste online',
          kept: 'No reembolsado',
          youGetBack: 'Recibes de vuelta',
          retainedDeposit: 'Es tu señal, que no es reembolsable.',
          retainedFee: 'Cubre la comisión de procesamiento del pago que se cobró al establecimiento y que no puede recuperar.',
          retainedLate: 'Las cancelaciones con menos antelación de la requerida no se reembolsan.',
          timing: 'Los reembolsos suelen tardar de 5 a 10 días hábiles en llegar a tu cuenta.',
          lateWarning: 'Esta cita empieza en menos de {{hours}} horas. Si cancelas ahora, lo que pagaste online no se devolverá.',
          lateNothingPaid: 'Esta cita empieza en menos de {{hours}} horas. Si puedes, avisa al establecimiento.',
          blockedTooLate: 'Esta cita ya no se puede cancelar online porque empieza en menos de {{hours}} horas. Ponte en contacto con el establecimiento.',
          blockedStarted: 'Esta cita ya ha empezado.',
          blockedCancelled: 'Esta cita ya está cancelada.',
          blockedCompleted: 'Esta cita ya está completada.',
          blockedGeneric: 'Esta cita ya no se puede cancelar online. Ponte en contacto con el establecimiento.',
          successWithRefund: 'Cita cancelada. Se reembolsarán {{amount}} a tu método de pago original.',
          successPending: 'Cita cancelada. Tu reembolso se está procesando y llegará a tu método de pago original.',
        },
        payTerms: {
          title: 'Antes de pagar',
          amount: 'Vas a pagar {{amount}} online.',
          continue: 'Continuar al pago',
        },
      },
    },
  },
  managementAppointments: {
    paymentStatus: {
      depositPaid: 'Señal pagada',
      refunded: 'Reembolsado',
      partiallyRefunded: 'Reembolsado en parte',
      disputed: 'En disputa',
    },
    prepayment: {
      paidOnline: 'Pagado online',
      nonRefundableDeposit: 'Señal no reembolsable',
      balanceDueInStore: 'El resto se paga en el establecimiento.',
      productsPaidOnline: 'Productos pagados online',
      productsPaidOnlineLocked: 'El cliente ya pagó estos productos online, así que no se pueden cambiar. Reembolsa primero el pago online si hay que cambiarlos.',
    },
    confirm: {
      cancelPaidOnlineMessage: 'El cliente pagó online y se le reembolsará el total, incluida la comisión de procesamiento del pago, que asume tu establecimiento. Esto libera el horario y no se puede deshacer.',
      noShowPaidOnlineMessage: 'El cliente pagó online. Al marcar que no se presentó, el pago se mantiene y no se reembolsa nada.',
    },
    delete: {
      paidOnline: 'Esta cita se pagó online. Cancélala en lugar de eliminarla, para que se reembolse al cliente.',
    },
  },
  paymentSettingsPage: {
    customerPayments: {
      error: {
        prepaymentServices: 'Algunos servicios piden señal o pago anticipado. Configúralos sin pago anticipado antes de desactivar el pago online de servicios.',
      },
      statementDescriptor: {
        title: 'Nombre en el extracto bancario',
        help: 'Es lo que los clientes ven en el extracto de su banco o tarjeta. Usa el nombre por el que conocen tu establecimiento, para que reconozcan el cargo.',
        label: 'Nombre en el extracto',
        preview: 'Los clientes verán',
        invalid: 'Usa de 5 a 22 letras, números o espacios, con al menos una letra.',
        save: 'Guardar nombre en el extracto',
        success: 'Nombre en el extracto guardado.',
        error: 'No se ha podido guardar el nombre en el extracto. Prueba con un nombre más corto y sin símbolos.',
      },
    },
  },
};
